import React, { useCallback, useMemo } from 'react';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import {
  CatalogRoom,
  ConflictResolution,
  ExtractedDateRange,
  RateConflictInfo,
  StopsellDateRange,
  StopsellRoom,
} from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';
import { pluralizeToString } from '~/utils/stringUtils';

import AddDateSection from './AddDateSection';
import StopsellExtractedDateRow from './StopsellExtractedDateRow';

interface Props {
  room: StopsellRoom;
  localDates: Array<ExtractedDateRange>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  blackoutDates: Array<StopsellDateRange>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  rateConflicts: Array<Array<RateConflictInfo>>;
  rateResolutions: Record<string, ConflictResolution>;
  onChange: (dateIndex: number, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
  onRoomTypeChange: (roomTypeId: string) => void;
  onResolve: (dateId: string, rateId: string, status: ConflictResolution) => void;
  onUndo: (dateId: string, rateId: string) => void;
  onEditDate: (dateId: string, newRange: { start: string; end: string }) => void;
  onDeleteDate: (dateId: string) => void;
  onAddExtractedDate: (start: string, end: string) => void;
}

export default function StopsellRoomGroup({
  room,
  localDates,
  catalogRooms,
  selectedRoomTypeId,
  blackoutDates,
  catalogRoomRates,
  rateConflicts,
  rateResolutions,
  onChange,
  onToggleAll,
  onRoomTypeChange,
  onResolve,
  onUndo,
  onEditDate,
  onDeleteDate,
  onAddExtractedDate,
}: Props) {
  const selectedCount = localDates.filter((date) => date.selected).length;
  const allSelected = localDates.length > 0 && selectedCount === localDates.length;
  const anySelected = selectedCount > 0;
  const indeterminate = anySelected && !allSelected;

  const handleToggleAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleAll(!allSelected);
    },
    [allSelected, onToggleAll],
  );

  const isMapped = !!selectedRoomTypeId;

  const today = useMemo(() => toDayJs(new Date().toISOString().slice(0, 10)), []);
  const futureBlackoutDates = blackoutDates.filter((d) => !toDayJs(d.end).isBefore(today, 'day'));
  const existingLabel =
    futureBlackoutDates.length > 0 ? futureBlackoutDates.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Accordion defaultExpanded variant="outlined" disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: 'grey.50' }}
        component="div"
        role="button"
        tabIndex={0}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, mr: 1 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => {
              e.stopPropagation();
              onToggleAll(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
            <Typography fontWeight={500} variant="body2">
              {room.name}
            </Typography>
            {catalogRooms.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={selectedRoomTypeId}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onRoomTypeChange(e.target.value)}
                error={!isMapped}
                sx={{ fontSize: 12, maxWidth: 320 }}
              >
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.disabled">
                    Select room type…
                  </Typography>
                </MenuItem>
                {catalogRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {existingLabel && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <EventBusyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Existing: {existingLabel}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Chip label={pluralizeToString('date range', localDates.length)} size="small" sx={{ bgcolor: 'grey.200' }} />
          <Box>
            <Button size="small" onClick={handleToggleAll}>
              {allSelected ? 'Deselect all' : 'Select all dates'}
            </Button>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack>
          {localDates.map((date, dateIndex) => (
            <StopsellExtractedDateRow
              key={date.id}
              date={date}
              dateIndex={dateIndex}
              catalogRoomRates={catalogRoomRates}
              rateConflicts={rateConflicts[dateIndex] ?? []}
              rateResolutions={rateResolutions}
              onToggle={onChange}
              onEdit={onEditDate}
              onDelete={onDeleteDate}
              onResolve={onResolve}
              onUndo={onUndo}
            />
          ))}
        </Stack>
        <Box sx={{ px: 5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <AddDateSection onAdd={onAddExtractedDate} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
